// apps/files-manager/src/pages/Properties.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Home,
  MapPin,
  DollarSign,
  Bed,
  Bath,
  RefreshCw,
  Loader2,
  Grid,
  List,
  Clock,
  Building2,
  ArrowUpDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  User,
  FileText,
  Compass,
  Sofa,
  Filter,
  Download,
  Copy,
  Link2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbaseClient';
import { useAuth } from '../contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Facing Directions mapping with emojis
const FACING_DIRECTIONS = {
  North: '?? North',
  South: '?? South',
  East: '?? East',
  West: '?? West',
  'North-East': '?? North-East',
  'North-West': '?? North-West',
  'South-East': '?? South-East',
  'South-West': '?? South-West',
};

const Properties = () => {
  const { user } = useAuth(); // may be null if not logged in
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sellerFilter, setSellerFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Helper to get image URL using pb.files.getURL (most reliable)
  const getImageUrl = (property, imageName) => {
    if (!imageName || !property) return null;
    try {
      return pb.files.getURL(property, imageName);
    } catch (error) {
      console.error('Image URL error:', error);
      return null;
    }
  };

  // Fetch properties with filters
  // IMPORTANT: We removed `enabled: !!user` so that the query runs even if user is not logged in.
  // This is safe because the collection's listRule is empty (public read).
  // If you want to restrict access, set listRule accordingly and keep the enabled check.
  const {
    data: properties = [],
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ['fileManagerProperties', typeFilter, statusFilter, sellerFilter],
    queryFn: async () => {
      try {
        let filter = 'type != "inquiry"';
        if (typeFilter !== 'all') {
          filter += ` && type = "${typeFilter}"`;
        }
        if (statusFilter !== 'all') {
          filter += ` && status = "${statusFilter}"`;
        }
        if (sellerFilter !== 'all') {
          filter += ` && seller_id = "${sellerFilter}"`;
        }

        const records = await pb.collection('properties').getFullList({
          sort: '-created',
          filter: filter,
        });
        console.log('Fetched properties:', records); // debug log
        return records;
      } catch (error) {
        console.error('Error fetching properties:', error);
        toast.error('Failed to load properties');
        return [];
      }
    },
    // Remove or comment out the enabled line to always fetch
    // enabled: !!user, // <-- commented out to always fetch
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('properties').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['fileManagerProperties']);
      toast.success('Property deleted successfully');
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete property');
    },
  });

  // Get unique sellers for filter
  const uniqueSellers = [
    ...new Set(properties.filter((p) => p.seller_id).map((p) => p.seller_id)),
  ];

  // Format price with ? symbol
  const formatPrice = (price) => {
    if (!price) return '?0';
    if (price >= 10000000) return `?${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `?${(price / 100000).toFixed(1)} L`;
    return `?${Number(price).toLocaleString('en-IN')}`;
  };

  const formatPlotPrice = (price) => {
    if (!price) return '?0';
    return `?${Number(price).toLocaleString('en-IN')}`;
  };

  // Filter and sort properties
  const filteredProperties = properties
    .filter((p) => {
      const search = searchTerm.toLowerCase();
      return (
        p.title?.toLowerCase().includes(search) ||
        p.city?.toLowerCase().includes(search) ||
        p.location?.toLowerCase().includes(search) ||
        p.seller_name?.toLowerCase().includes(search) ||
        p.seller_company?.toLowerCase().includes(search) ||
        p.seller_id?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const direction = sortOrder === 'desc' ? -1 : 1;
      if (sortBy === 'price') {
        return (a.price - b.price) * direction;
      }
      if (sortBy === 'title') {
        return a.title?.localeCompare(b.title || '') * direction;
      }
      return (new Date(a.created) - new Date(b.created)) * direction;
    });

  // Badge helpers
  const getStatusBadge = (status) => {
    const statusMap = {
      active: {
        icon: CheckCircle,
        label: 'Active',
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      },
      draft: {
        icon: AlertCircle,
        label: 'Draft',
        color: 'bg-slate-100 text-slate-700 border-slate-200',
      },
      pending: {
        icon: Clock,
        label: 'Pending',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
      },
    };
    const config = statusMap[status] || statusMap.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getPossessionBadge = (status) => {
    const statusMap = {
      ready_to_move: { label: 'Ready to Move', color: 'bg-emerald-100 text-emerald-700' },
      completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
      under_construction: { label: 'Under Construction', color: 'bg-amber-100 text-amber-700' },
      upcoming: { label: 'Upcoming', color: 'bg-purple-100 text-purple-700' },
    };
    const config = statusMap[status] || statusMap.under_construction;
    return <Badge className={`${config.color} border-0 text-xs`}>{config.label}</Badge>;
  };

  const getTypeBadge = (type) => (type === 'flat' ? 'Apartment' : 'Plot');

  const getCategoryColor = (category) => {
    const colors = {
      Residential: 'bg-blue-100 text-blue-700',
      Commercial: 'bg-purple-100 text-purple-700',
      Industrial: 'bg-orange-100 text-orange-700',
      Agricultural: 'bg-green-100 text-green-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  const getSellerDisplay = (property) => {
    if (property.seller_name) return property.seller_name;
    if (property.seller_company) return property.seller_company;
    return 'N/A';
  };

  // Handlers
  const handleDeleteClick = (property) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (propertyToDelete) {
      deleteMutation.mutate(propertyToDelete.id);
    }
  };

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
    setDetailsDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-500">Failed to load properties. Please try again.</p>
          <Button onClick={() => refetch()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Home className="w-6 h-6 text-indigo-600" />
            Properties
          </h1>
          <p className="text-slate-500 text-sm">Manage all property listings</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/properties/new')}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Add Property
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-800">{properties.length}</div>
            <p className="text-sm text-slate-500">Total Properties</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-600">
              {properties.filter((p) => p.status === 'active').length}
            </div>
            <p className="text-sm text-slate-500">Active</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">
              {properties.filter((p) => p.status === 'pending').length}
            </div>
            <p className="text-sm text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-600">
              {properties.filter((p) => p.status === 'draft').length}
            </div>
            <p className="text-sm text-slate-500">Drafts</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by title, city, seller..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="flat">Flats / Apartments</SelectItem>
                <SelectItem value="plot">Plots / Land</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>

            {uniqueSellers.length > 0 && (
              <Select value={sellerFilter} onValueChange={setSellerFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Seller" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sellers</SelectItem>
                  {uniqueSellers.map((seller) => (
                    <SelectItem key={seller} value={seller}>
                      {seller}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="h-10 w-10 p-0"
                title="Toggle sort order"
              >
                <ArrowUpDown
                  className={`w-4 h-4 transition-transform ${
                    sortOrder === 'asc' ? 'rotate-180' : ''
                  }`}
                />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-10 w-10 p-0"
                title="Grid view"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-10 w-10 p-0"
                title="List view"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex justify-between items-center text-sm text-slate-500">
        <span>Showing {filteredProperties.length} properties</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()} className="gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      {/* Properties Grid / List */}
      {filteredProperties.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No properties found</p>
            <p className="text-sm text-slate-400">Add your first property listing</p>
            <Button
              onClick={() => navigate('/properties/new')}
              className="mt-4 gap-2 bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" /> Add Property
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProperties.map((property) => {
            const imageUrl = getImageUrl(property, property.featured_image);
            return (
              <Card
                key={property.id}
                className="hover:shadow-xl transition-all duration-300 overflow-hidden group border border-slate-200"
              >
                <CardContent className="p-0">
                  <div className="relative">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={property.title}
                        className="w-full h-52 object-cover group-hover:scale-105 transition duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-52 bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
                        <Home className="w-16 h-16 text-indigo-300" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">{getStatusBadge(property.status)}</div>
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      <Badge className="bg-white/90 backdrop-blur text-slate-700 border-0">
                        <Building2 className="w-3 h-3 mr-1" />
                        {getTypeBadge(property.type)}
                      </Badge>
                      {getPossessionBadge(property.possession_status)}
                      {property.category && (
                        <Badge
                          className={`${getCategoryColor(property.category)} border-0 text-xs`}
                        >
                          {property.category}
                        </Badge>
                      )}
                    </div>
                    {property.rera_registered && (
                      <div className="absolute bottom-2 right-2">
                        <Badge className="bg-emerald-500 text-white border-0 text-xs shadow-lg">
                          <Shield className="w-3 h-3 inline mr-1" /> RERA
                        </Badge>
                      </div>
                    )}
                    {property.brochure && (
                      <div className="absolute bottom-2 left-2">
                        <Badge className="bg-amber-500 text-white border-0 text-xs flex items-center gap-1 shadow-lg">
                          <FileText className="w-3 h-3" /> Brochure
                          {property.download_count > 0 && (
                            <span className="ml-1">({property.download_count})</span>
                          )}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-800 truncate group-hover:text-indigo-600 transition">
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            {property.city}, {property.location}
                          </span>
                        </div>
                        {(property.seller_name || property.seller_company) && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                            <User className="w-3 h-3" />
                            <span className="truncate">{getSellerDisplay(property)}</span>
                            {property.seller_id && (
                              <span className="text-slate-300">(ID: {property.seller_id})</span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-lg font-bold text-indigo-600 whitespace-nowrap">
                        {formatPrice(property.price)}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 mt-2 text-sm flex-wrap">
                      {property.type === 'flat' && (
                        <>
                          <span className="flex items-center gap-1 text-slate-600 bg-slate-50 px-2 py-1 rounded">
                            <Bed className="w-3 h-3 text-indigo-400" /> {property.bedrooms || 0} BHK
                          </span>
                          <span className="flex items-center gap-1 text-slate-600 bg-slate-50 px-2 py-1 rounded">
                            <Bath className="w-3 h-3 text-indigo-400" /> {property.bathrooms || 0}
                          </span>
                          {property.carpet_area && (
                            <span className="text-slate-500 text-xs bg-slate-50 px-2 py-1 rounded">
                              {property.carpet_area} sq.ft.
                            </span>
                          )}
                          {property.balconies && property.balconies > 0 && (
                            <span className="text-slate-500 text-xs flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                              <Sofa className="w-3 h-3 text-indigo-400" /> {property.balconies}
                            </span>
                          )}
                        </>
                      )}
                      {property.type === 'plot' && (
                        <>
                          {property.plot_area && (
                            <span className="text-slate-500 text-xs bg-slate-50 px-2 py-1 rounded">
                              {property.plot_area} sq.ft.
                            </span>
                          )}
                          {property.facing_direction && (
                            <span className="text-slate-500 text-xs flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                              <Compass className="w-3 h-3 text-indigo-400" />
                              {FACING_DIRECTIONS[property.facing_direction] ||
                                property.facing_direction}
                            </span>
                          )}
                          {property.plot_price && (
                            <span className="text-slate-500 text-xs bg-slate-50 px-2 py-1 rounded">
                              {formatPlotPrice(property.plot_price)}/sq.ft.
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
                      <div className="text-xs text-slate-400">
                        {new Date(property.created).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-indigo-50 hover:text-indigo-600"
                          onClick={() => handleViewDetails(property)}
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-indigo-50 hover:text-indigo-600"
                          onClick={() => navigate(`/properties/edit/${property.id}`)}
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                          onClick={() => handleDeleteClick(property)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Property
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Seller
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProperties.map((property) => {
                  const imageUrl = getImageUrl(property, property.featured_image);
                  return (
                    <tr key={property.id} className="hover:bg-slate-50 transition group">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={property.title}
                                className="w-full h-full object-cover"
                                onError={(e) => (e.target.style.display = 'none')}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
                                <Home className="w-4 h-4 text-indigo-300" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800 text-sm truncate max-w-[200px]">
                              {property.title}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {property.rera_registered && (
                                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                                  RERA
                                </Badge>
                              )}
                              {property.brochure && (
                                <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">
                                  <FileText className="w-3 h-3 inline mr-0.5" />
                                  Brochure
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {getTypeBadge(property.type)}
                        </Badge>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {getPossessionBadge(property.possession_status)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {property.category && (
                          <Badge
                            className={`${getCategoryColor(property.category)} border-0 text-xs`}
                          >
                            {property.category}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="w-3 h-3" /> {property.city}
                        </div>
                        <div className="text-xs text-slate-400 truncate max-w-[120px]">
                          {property.location}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-indigo-600">
                          {formatPrice(property.price)}
                        </div>
                        {property.type === 'plot' && property.plot_price && (
                          <div className="text-xs text-slate-400">
                            {formatPlotPrice(property.plot_price)}/sq.ft.
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm text-slate-700">
                            {getSellerDisplay(property)}
                          </span>
                          {property.seller_id && (
                            <span className="text-xs text-slate-400">ID: {property.seller_id}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(property.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => handleViewDetails(property)}
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => navigate(`/properties/edit/${property.id}`)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                            onClick={() => handleDeleteClick(property)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Property
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold text-slate-800">
                "{propertyToDelete?.title}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Property Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-800">
              Property Details
            </DialogTitle>
            <DialogDescription>Full information about this property</DialogDescription>
          </DialogHeader>
          {selectedProperty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Title</p>
                  <p className="font-semibold text-slate-800">{selectedProperty.title}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Slug</p>
                  <p className="font-mono text-sm text-slate-600">{selectedProperty.slug}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">City</p>
                  <p className="text-slate-700">{selectedProperty.city}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="text-slate-700">{selectedProperty.location}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Type</p>
                  <p className="text-slate-700 capitalize">{selectedProperty.type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Category</p>
                  <p className="text-slate-700">{selectedProperty.category || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Price</p>
                  <p className="font-bold text-indigo-600">{formatPrice(selectedProperty.price)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  {getStatusBadge(selectedProperty.status)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Possession Status</p>
                  {getPossessionBadge(selectedProperty.possession_status)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">RERA Registered</p>
                  <p>{selectedProperty.rera_registered ? '? Yes' : '? No'}</p>
                </div>
                {selectedProperty.type === 'flat' && (
                  <>
                    <div>
                      <p className="text-sm text-slate-500">Bedrooms</p>
                      <p>{selectedProperty.bedrooms || 0} BHK</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Bathrooms</p>
                      <p>{selectedProperty.bathrooms || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Carpet Area</p>
                      <p>{selectedProperty.carpet_area || 'N/A'} sq.ft.</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Balconies</p>
                      <p>{selectedProperty.balconies || 0}</p>
                    </div>
                  </>
                )}
                {selectedProperty.type === 'plot' && (
                  <>
                    <div>
                      <p className="text-sm text-slate-500">Plot Area</p>
                      <p>{selectedProperty.plot_area || 'N/A'} sq.ft.</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Road Width</p>
                      <p>{selectedProperty.road_width || 'N/A'} feet</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Facing Direction</p>
                      <p>
                        {FACING_DIRECTIONS[selectedProperty.facing_direction] ||
                          selectedProperty.facing_direction ||
                          'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Plot Price</p>
                      <p>{formatPlotPrice(selectedProperty.plot_price)}/sq.ft.</p>
                    </div>
                  </>
                )}
                {selectedProperty.seller_name && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Seller</p>
                    <p className="text-slate-700">
                      {selectedProperty.seller_name}
                      {selectedProperty.seller_company
                        ? ` (${selectedProperty.seller_company})`
                        : ''}
                    </p>
                    {selectedProperty.seller_phone && (
                      <p className="text-sm text-slate-500">Phone: {selectedProperty.seller_phone}</p>
                    )}
                    {selectedProperty.seller_email && (
                      <p className="text-sm text-slate-500">Email: {selectedProperty.seller_email}</p>
                    )}
                  </div>
                )}
              </div>
              {selectedProperty.description && (
                <div className="col-span-2 pt-2 border-t">
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="text-slate-700 text-sm">{selectedProperty.description}</p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                  Close
                </Button>
                <Button
                  onClick={() => navigate(`/properties/edit/${selectedProperty.id}`)}
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                >
                  <Edit className="w-4 h-4" /> Edit Property
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Properties;